/**
 * §A.2 · Quién no nos ha pagado.
 *
 * **Esta pantalla es H-14 hecha herramienta.** Sin pasarela no hay webhook que
 * diga que un club ha pagado: nos hacen una transferencia o un Bizum y alguien lo
 * marca. Si no existe un sitio donde ver de un vistazo quién falta, esa decisión
 * se convierte en una hoja de cálculo al mes.
 *
 * ## No hay endpoint de «morosos de plataforma», y no hace falta
 *
 * Lo que hay es la lista de clubes con su plan, y los cobros de cada uno. Con
 * cinco o cincuenta clubes, cruzarlo aquí es una vuelta por la lista; construir un
 * endpoint para eso sería diseñar antes de saber si la pregunta se sostiene.
 *
 * **El límite está escrito y es doscientos clubes**: a partir de ahí esto pide
 * `GET /admin/academies/unpaid` y deja de pedir n+1 peticiones. Queda anotado para
 * que se note al pasarlo, no para que se descubra.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import type {
  ClubResumen,
  PaginaDePagos,
  ResumenDePagos,
} from '@/backoffice/tipos';

/** El precio mensual equivalente: un plan anual o trimestral se divide, para poder sumar. */
const alMes = (c: ClubResumen): number => {
  if (c.price_cents === null) return 0;
  if (c.billing_period === 'annual') return Math.round(c.price_cents / 12);
  if (c.billing_period === 'quarterly') return Math.round(c.price_cents / 3);
  return c.price_cents;
};

/**
 * Cuántos días lleva sin pagar.
 *
 * `null` si no ha pagado nunca, que **no** es cero días: uno es un club que acaba
 * de entrar y el otro es uno que se ha ido dejando de pagar.
 */
function diasDesde(ultimo: Date | string | null): number | null {
  if (!ultimo) return null;
  const d = ultimo instanceof Date ? ultimo : new Date(ultimo);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

/** «hoy», «ayer», «hace 12 días». Un «hace 0 días» no se lee como hoy. */
const haceCuanto = (dias: number): string => {
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
};

export function CobrosDePlataforma({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const clubes = useQuery({
    queryKey: ['admin', 'clubes', 'cobrables'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.academies.get({
        query: { limit: 200 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      // Un club que se fue no nos debe nada; uno de prueba tampoco todavía.
      return (data as unknown as ClubResumen[]).filter(
        (c) => c.status === 'active' || c.status === 'suspended'
      );
    },
  });

  /*
   * Un resumen por club. `useQueries` y no un bucle de `useQuery`: el número de
   * peticiones cambia con la lista, y los hooks no se pueden llamar en un bucle
   * de longitud variable.
   */
  const resumenes = useQueries({
    queries: (clubes.data ?? []).map((c) => ({
      queryKey: ['admin', 'pagos-resumen', c.id],
      queryFn: async () => {
        const { data, error } = await api.v1.admin
          .academies({ id: c.id })
          .payments.get({ query: { limit: 1 } });
        if (error) throw new ErrorDeApi(aProblema(error));
        return (data as unknown as PaginaDePagos).summary;
      },
    })),
  });

  if (clubes.isPending) {
    return <Cargando filas={6} etiqueta="Cargando los clubes" />;
  }

  if (clubes.isError || !clubes.data) {
    return (
      <PanelError
        problema={aProblema(clubes.error)}
        reintentar={() => void clubes.refetch()}
      />
    );
  }

  const cargando = resumenes.some((r) => r.isPending);

  const filas = clubes.data.map((c, i) => {
    const resumen = resumenes[i]?.data as ResumenDePagos | undefined;
    return { club: c, resumen, dias: diasDesde(resumen?.last_paid_on ?? null) };
  });

  // De lo más antiguo a lo más reciente, y los que no han pagado nunca los
  // primeros: es el orden en que hay que escribirles.
  const ordenadas = [...filas].sort((a, b) => {
    if (a.dias === null && b.dias === null) return 0;
    if (a.dias === null) return -1;
    if (b.dias === null) return 1;
    return b.dias - a.dias;
  });

  const contratado = clubes.data.reduce((s, c) => s + alMes(c), 0);
  const cobrado = filas.reduce(
    (s, f) => s + (f.resumen?.collected_cents ?? 0),
    0
  );

  return (
    <section>
      <header className="titulo-fila">
        <h1>Cobros</h1>
      </header>

      <p className="tenue">
        Lo que nos deberían pagar cada mes y lo que ha entrado de verdad. Sin
        pasarela lo marca una persona, así que este es el sitio donde se ve
        quién falta.
      </p>

      {cargando ? (
        <Cargando filas={2} etiqueta="Sumando los cobros" />
      ) : (
        <div className="aviso aviso-calma" role="status">
          <strong>{euros(contratado)} al mes contratados</strong> entre{' '}
          {clubes.data.length}{' '}
          {clubes.data.length === 1 ? 'club activo' : 'clubes activos'}, y{' '}
          <strong>{euros(cobrado)} cobrados</strong> en todo el histórico.
          <p className="tenue">
            Las dos cifras no se comparan entre sí: una es mensual y la otra es
            todo lo que ha entrado desde el principio.
          </p>
        </div>
      )}

      {clubes.data.length === 0 && (
        <Vacio
          titulo="Ningún club activo todavía"
          explicacion="Cuando haya clubes de pago, aquí se verá quién ha pagado y quién no."
        />
      )}

      {clubes.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Club</th>
                <th className="num">Contratado</th>
                <th className="num">Cobrado</th>
                <th className="num">Pagos</th>
                <th>Último</th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map(({ club: c, resumen, dias }) => (
                <tr
                  key={c.id}
                  className={
                    // Nunca ha pagado, o hace más de 45 días. Un mes justo no es
                    // deuda: los cobros se anotan con retraso, y teñir a quien pagó
                    // el día 3 sería enseñar un rojo que no significa nada.
                    dias === null || dias > 45 ? 'fila-aviso' : undefined
                  }
                >
                  <td>
                    <button
                      type="button"
                      className="enlace-fila"
                      onClick={() => alAbrirClub(c.id)}
                    >
                      {c.trade_name}
                    </button>
                    <div className="tenue">{c.plan_name ?? 'Sin plan'}</div>
                  </td>
                  <td className="num">
                    {c.price_cents === null ? '—' : euros(alMes(c))}
                    <span className="tenue">/mes</span>
                  </td>
                  <td className="num">
                    {resumen ? euros(resumen.collected_cents) : '…'}
                  </td>
                  <td className="num tenue">{resumen?.payments ?? '…'}</td>
                  <td
                    className={dias === null || dias > 45 ? undefined : 'tenue'}
                  >
                    {!resumen ? (
                      '…'
                    ) : dias === null ? (
                      <strong>Nunca ha pagado</strong>
                    ) : (
                      <>
                        {fecha(resumen.last_paid_on)}
                        <span className="tenue"> · {haceCuanto(dias)}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="tenue">
        Para anotar un cobro, abre el club. Esta lista pide el resumen de cada
        uno por separado: con más de doscientos clubes hay que cambiarla por un
        endpoint que lo resuelva de una vez.
      </p>
    </section>
  );
}
